import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/Localizacao.module.css';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, FaDirections, FaHome } from 'react-icons/fa';

const Localizacao = () => {
  const endereco = {
    rua: "R. Santa Rosália, 276",
    bairro: "Jardim Santa Rosália",
    cidade: "Sorocaba",
    estado: "SP",
    cep: "18095-040",
    telefone: ["(15) 99715-2102", "(15) 98822-2423"],
    email: "maikeramos.juridico@gmail.com",
    horario: "Segunda a Sexta: 08h às 18h"
  };
  
  const latitude = -23.493394375077266; 
  const longitude = -47.45657385346649;
  
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  const [mapWasMoved, setMapWasMoved] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
    }

    let L;
    import('leaflet').then(leaflet => {
      L = leaflet;
      import('leaflet/dist/leaflet.css');
      
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png',
      });

      if (!mapRef.current) return;
      
      if (mapRef.current.clientHeight === 0) {
        mapRef.current.style.height = isMobile ? '350px' : '450px';
      }
      
      const map = L.map(mapRef.current, {
        zoomControl: true,
        dragging: true,
        scrollWheelZoom: !isMobile,
        doubleClickZoom: true
      }).setView([latitude, longitude], 16);
      
      mapInstanceRef.current = map;
      
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      
      L.marker([latitude, longitude])
        .addTo(map)
        .bindPopup(`<b>Nosso Escritório</b><br>${endereco.rua}`);
        
      map.on('movestart', () => {
        setMapWasMoved(true);
      });
      
      map.on('zoomstart', () => {
        setMapWasMoved(true);
      });
      
      setTimeout(() => {
        map.invalidateSize();
      }, 300);
    });
    
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [endereco.rua, latitude, longitude, isMobile]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && mapInstanceRef.current) {
        setTimeout(() => {
          mapInstanceRef.current.invalidateSize();
        }, 300);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const abrirNoMapaNativo = () => {
    const url = 'https://maps.app.goo.gl/vQbC5xGkaEoV4Lhb8';
    window.open(url, '_blank');
  };
  
  const recentralizarMapa = () => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([latitude, longitude], 16, {
        animate: true,
        duration: 1
      });
      setMapWasMoved(false);
    }
  };
  
  const formatPhoneForHref = (phone) => {
    return phone.replace(/[^0-9+]/g, '');
  };

  return (
    <section className={styles.localizacao} id="localizacao">
      <div className={styles.container}>
        <h2 className={styles.title}>Nossa Localização</h2>
        
        <div className={styles.content}>
          <div className={styles.infoSection}>
            <div className={styles.infoCard}>
              <h3>Informações de Contato</h3>
              
              <div className={styles.infoItem}>
                <FaMapMarkerAlt className={styles.icon} />
                <div>
                  <a href={`https://maps.app.goo.gl/vQbC5xGkaEoV4Lhb8`} target="_blank" rel="noopener noreferrer">
                    <p>{endereco.rua}</p>
                    <p>{endereco.bairro} - {endereco.cidade}/{endereco.estado}</p>
                    <p>{endereco.cep}</p>
                  </a>
                </div>
              </div>
              
              <div className={styles.infoItem}>
                <FaPhone className={styles.icon} />
                <div>
                  {endereco.telefone.map((telefone, index) => (
                    <a key={index} href={`tel:${formatPhoneForHref(telefone)}`}>
                      {telefone}{index < endereco.telefone.length - 1 ? ', ' : ''}
                    </a>
                  ))}
                </div>
              </div>
              
              <div className={styles.infoItem}>
                <FaEnvelope className={styles.icon} />
                <a href={`mailto:${endereco.email}`}>{endereco.email}</a>
              </div>
              
              <div className={styles.infoItem}>
                <FaClock className={styles.icon} />
                <p>{endereco.horario}</p>
              </div>
              
              <div className={styles.actions}>
                <button 
                  className={styles.directionButton}
                  onClick={abrirNoMapaNativo}
                >
                  <FaDirections /> Como Chegar
                </button>
              </div>
            </div>
          </div>
          
          <div className={styles.mapContainer}>
            <div ref={mapRef} className={styles.map}></div>
            {mapWasMoved && (
              <button 
                className={styles.recenterButton}
                onClick={recentralizarMapa}
                title="Voltar para a localização do escritório"
              >
                <FaHome /> Recentralizar
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Localizacao;